import { Badge, BadgeEvaluator } from './types';
import { Plant, PlantVariety, Species, Genus } from '../../types/plant';
import { PlantSource } from '../../types/plantSource';
import { User } from '../../types/auth';

// Type definitions for badge evaluation
interface PlantWithRelations extends Plant {
  variety?: PlantVariety & {
    species?: Species & {
      genus?: Genus;
    };
  };
  source?: PlantSource;
  user?: User;
}

interface UserStatsData {
  accountAge?: number;
  sourcesShared?: number;
}

// Icon mappings for common badge types
const BADGE_ICONS = {
  // Plant badges
  'globe-americas': 'GlobeAmericasIcon',
  star: 'StarIcon',
  sparkles: 'SparklesIcon',
  bug: 'BugAntIcon',
  leaf: 'CakeIcon', // Using cake icon for edible plants
  beaker: 'BeakerIcon',
  home: 'HomeIcon',
  sun: 'SunIcon',

  // Achievement badges
  flag: 'FlagIcon',
  'magnifying-glass': 'MagnifyingGlassIcon',
  collection: 'RectangleStackIcon',
  'academic-cap': 'AcademicCapIcon',
  'archive-box': 'ArchiveBoxIcon',
  'map-pin': 'MapPinIcon',
  calendar: 'CalendarIcon',
  trophy: 'TrophyIcon',
  book: 'BookOpenIcon',

  // Milestone badges
  gift: 'GiftIcon',
  'qr-code': 'QrCodeIcon',
  users: 'UsersIcon',
} as const;

// Badge evaluators for frontend-only evaluation (when needed)
const createPlantEvaluator = (
  evaluateFn: (plant: PlantWithRelations) => boolean
): BadgeEvaluator => ({
  type: 'plant',
  evaluate: evaluateFn,
});

const createUserEvaluator = (evaluateFn: (userData: UserStatsData) => boolean): BadgeEvaluator => ({
  type: 'user',
  evaluate: evaluateFn,
});

const createCollectionEvaluator = (
  evaluateFn: (plants: PlantWithRelations[]) => boolean
): BadgeEvaluator => ({
  type: 'collection',
  evaluate: evaluateFn,
});

// Badge definitions matching backend implementation
export const BADGE_DEFINITIONS: Badge[] = [
  // Plant-specific badges
  {
    id: 'native-plant',
    name: 'Native Plant',
    description: 'This plant is native to your region',
    icon: BADGE_ICONS['globe-americas'],
    variant: 'gold',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('native') ||
        variety?.plantType?.toLowerCase().includes('native') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('native'))
      );
    }),
  },
  {
    id: 'heirloom',
    name: 'Heirloom Variety',
    description: 'Traditional heritage plant variety',
    icon: BADGE_ICONS['star'],
    variant: 'warning',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('heirloom') ||
        variety?.plantType?.toLowerCase().includes('heirloom') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('heirloom'))
      );
    }),
  },
  {
    id: 'rare-species',
    name: 'Rare Species',
    description: 'Uncommon or endangered plant species',
    icon: BADGE_ICONS['sparkles'],
    variant: 'purple',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('rare') ||
        variety?.plantType?.toLowerCase().includes('rare') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('rare'))
      );
    }),
  },
  {
    id: 'pollinator',
    name: 'Pollinator Friendly',
    description: 'Attracts bees, butterflies, and other pollinators',
    icon: BADGE_ICONS['bug'],
    variant: 'info',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('pollinator') ||
        variety?.plantType?.toLowerCase().includes('pollinator') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('pollinator'))
      );
    }),
  },
  {
    id: 'edible',
    name: 'Edible Plant',
    description: 'Produces edible fruits, vegetables, or herbs',
    icon: BADGE_ICONS['leaf'],
    variant: 'success',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      const edibleCategories = ['vegetable', 'herb', 'fruit', 'berry', 'spice'];
      return !!edibleCategories.some(
        cat =>
          variety?.category?.toLowerCase().includes(cat) ||
          variety?.plantType?.toLowerCase().includes(cat)
      );
    }),
  },
  {
    id: 'medicinal',
    name: 'Medicinal Plant',
    description: 'Traditional medicinal or therapeutic properties',
    icon: BADGE_ICONS['beaker'],
    variant: 'pink',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('medicinal') ||
        variety?.plantType?.toLowerCase().includes('medicinal') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('medicinal'))
      );
    }),
  },
  {
    id: 'indoor',
    name: 'Indoor Plant',
    description: 'Suitable for indoor growing conditions',
    icon: BADGE_ICONS['home'],
    variant: 'indigo',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('indoor') ||
        variety?.plantType?.toLowerCase().includes('houseplant') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('indoor'))
      );
    }),
  },
  {
    id: 'drought-tolerant',
    name: 'Drought Tolerant',
    description: 'Thrives with minimal water requirements',
    icon: BADGE_ICONS['sun'],
    variant: 'warning',
    category: 'plant',
    evaluator: createPlantEvaluator(plant => {
      const variety = plant.variety;
      return !!(
        variety?.category?.toLowerCase().includes('drought') ||
        variety?.plantType?.toLowerCase().includes('succulent') ||
        variety?.alternativeNames?.some(name => name.toLowerCase().includes('drought'))
      );
    }),
  },

  // User achievement badges
  {
    id: 'garden-starter',
    name: 'Garden Starter',
    description: 'Added your first plant to the garden',
    icon: BADGE_ICONS['flag'],
    variant: 'success',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => plants.length >= 1),
  },
  {
    id: 'variety-hunter',
    name: 'Variety Hunter',
    description: 'Collected 5 or more different plant varieties',
    icon: BADGE_ICONS['magnifying-glass'],
    variant: 'purple',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      const uniqueVarieties = new Set(
        plants.filter(p => p.plantVarietyId).map(p => p.plantVarietyId)
      );
      return uniqueVarieties.size >= 5;
    }),
  },
  {
    id: 'species-collector',
    name: 'Species Collector',
    description: 'Collected 10 or more different species',
    icon: BADGE_ICONS['collection'],
    variant: 'info',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      const uniqueSpecies = new Set(
        plants.filter(p => p.variety?.speciesId).map(p => p.variety!.speciesId)
      );
      return uniqueSpecies.size >= 10;
    }),
  },
  {
    id: 'genus-explorer',
    name: 'Genus Explorer',
    description: 'Explored 5 or more different plant genera',
    icon: BADGE_ICONS['academic-cap'],
    variant: 'indigo',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      const uniqueGenera = new Set(
        plants.filter(p => p.variety?.species?.genusId).map(p => p.variety!.species!.genusId)
      );
      return uniqueGenera.size >= 5;
    }),
  },
  {
    id: 'seed-saver',
    name: 'Seed Saver',
    description: 'Growing plants from your own saved seeds',
    icon: BADGE_ICONS['archive-box'],
    variant: 'success',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      return plants.some(
        p =>
          p.source?.name?.toLowerCase().includes('seed') ||
          p.source?.name?.toLowerCase().includes('saved') ||
          p.notes?.toLowerCase().includes('seed')
      );
    }),
  },
  {
    id: 'local-sourcer',
    name: 'Local Sourcer',
    description: 'Supporting local nurseries and garden centers',
    icon: BADGE_ICONS['map-pin'],
    variant: 'info',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      return plants.some(
        p =>
          p.source?.name?.toLowerCase().includes('local') ||
          p.source?.name?.toLowerCase().includes('nursery')
      );
    }),
  },
  {
    id: 'season-grower',
    name: 'Season Grower',
    description: 'Growing plants across all four seasons',
    icon: BADGE_ICONS['calendar'],
    variant: 'warning',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      const months = plants
        .filter(p => p.plantedDate)
        .map(p => new Date(p.plantedDate!).getMonth());
      const seasons = new Set();
      months.forEach(month => {
        if (month >= 2 && month <= 4) seasons.add('spring');
        else if (month >= 5 && month <= 7) seasons.add('summer');
        else if (month >= 8 && month <= 10) seasons.add('fall');
        else seasons.add('winter');
      });
      return seasons.size >= 4;
    }),
  },
  {
    id: 'centurion',
    name: 'Centurion',
    description: 'Tracking 100 or more plants',
    icon: BADGE_ICONS['trophy'],
    variant: 'danger',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => plants.length >= 100),
  },
  {
    id: 'botanical-scholar',
    name: 'Botanical Scholar',
    description: 'All plants have complete taxonomic information',
    icon: BADGE_ICONS['book'],
    variant: 'purple',
    category: 'achievement',
    evaluator: createCollectionEvaluator(plants => {
      if (plants.length === 0) return false;
      return plants.every(
        p =>
          p.variety?.species?.genus?.name &&
          p.variety?.species?.name &&
          p.variety?.primaryCommonName
      );
    }),
  },

  // Milestone badges
  {
    id: 'anniversary',
    name: 'Anniversary Gardener',
    description: 'Active gardener for over a year',
    icon: BADGE_ICONS['gift'],
    variant: 'pink',
    category: 'milestone',
    evaluator: createUserEvaluator(userData => {
      const accountAge = userData.accountAge || 0;
      return accountAge >= 365;
    }),
  },
  {
    id: 'qr-pioneer',
    name: 'QR Pioneer',
    description: 'Generated 50 or more plant QR codes',
    icon: BADGE_ICONS['qr-code'],
    variant: 'indigo',
    category: 'milestone',
    evaluator: createCollectionEvaluator(plants => {
      const qrCodeCount = plants.filter(p => p.qrCodeId).length;
      return qrCodeCount >= 50;
    }),
  },
  {
    id: 'community',
    name: 'Community Contributor',
    description: 'Shared plant sources with the community',
    icon: BADGE_ICONS['users'],
    variant: 'success',
    category: 'milestone',
    evaluator: createUserEvaluator(userData => {
      const sourcesShared = userData.sourcesShared || 0;
      return sourcesShared >= 1;
    }),
  },
];

// Types for badge metadata
interface ProgressData {
  plantCount: number;
  varietyCount: number;
  speciesCount: number;
  genusCount: number;
  qrCodeCount: number;
  sourcesShared: number;
  accountAge: number; // in days
}

// Badge metadata for UI components (Phase 1)
interface BadgeMetadataConfig {
  criteria: string[];
  tips?: string;
  progressConfig?: {
    statsField: keyof ProgressData;
    maxValue: number;
    unit: string;
  };
  requiresBackendTracking?: boolean;
}

export const BADGE_METADATA: Record<string, BadgeMetadataConfig> = {
  // Plant badges
  'native-plant': {
    criteria: [
      'Plant variety is marked as native to your region',
      'Plant type or category contains "native"',
      'Alternative names include native plant references',
    ],
  },
  heirloom: {
    criteria: [
      'Plant variety is classified as heirloom',
      'Category contains "heirloom" or "heritage"',
      'Historical plant variety with established lineage',
    ],
  },
  'rare-species': {
    criteria: [
      'Plant species is uncommon or rare',
      'Variety is marked as rare or endangered',
      'Limited availability or special conservation status',
    ],
  },
  pollinator: {
    criteria: [
      'Plant attracts bees, butterflies, or other pollinators',
      'Category includes pollinator-friendly designation',
      'Known to support local pollinator populations',
    ],
  },
  edible: {
    criteria: [
      'Plant type is vegetable, herb, or fruit',
      'Category indicates edible parts',
      'Produces food for human consumption',
    ],
  },
  medicinal: {
    criteria: [
      'Plant has medicinal properties',
      'Category includes medicinal designation',
      'Traditionally used for health and wellness',
    ],
  },
  indoor: {
    criteria: [
      'Plant is suitable for indoor growing',
      'Category indicates houseplant or indoor variety',
      'Thrives in controlled indoor environments',
    ],
  },
  'drought-tolerant': {
    criteria: [
      'Plant requires minimal water',
      'Category indicates drought tolerance',
      'Adapted to low-water conditions',
    ],
  },

  // Achievement badges
  'garden-starter': {
    criteria: ['Add your first plant to the collection'],
    tips: 'Add your first plant to get started!',
    progressConfig: {
      statsField: 'plantCount',
      maxValue: 1,
      unit: 'plants',
    },
  },
  'variety-hunter': {
    criteria: ['Collect 5 or more different plant varieties'],
    tips: 'Try planting different varieties of the same species, like multiple tomato varieties.',
    progressConfig: {
      statsField: 'varietyCount',
      maxValue: 5,
      unit: 'varieties',
    },
  },
  'species-collector': {
    criteria: ['Track 10 or more different plant species'],
    tips: 'Explore different plant species across various families and genera.',
    progressConfig: {
      statsField: 'speciesCount',
      maxValue: 10,
      unit: 'species',
    },
  },
  'genus-explorer': {
    criteria: ['Grow plants from 5 or more different genera'],
    tips: 'Branch out into different plant families - try herbs, vegetables, flowers, and trees.',
    progressConfig: {
      statsField: 'genusCount',
      maxValue: 5,
      unit: 'genera',
    },
  },
  centurion: {
    criteria: ['Track 100 or more plants in your collection'],
    tips: 'Keep expanding your collection - every plant counts toward this milestone!',
    progressConfig: {
      statsField: 'plantCount',
      maxValue: 100,
      unit: 'plants',
    },
  },
  'qr-pioneer': {
    criteria: ['Generate 50 or more QR codes for your plants'],
    tips: 'Generate QR codes for your plants to reach 50 codes.',
    progressConfig: {
      statsField: 'qrCodeCount',
      maxValue: 50,
      unit: 'QR codes',
    },
  },
  'seed-saver': {
    criteria: ['Add plants grown from saved seeds', 'Source plants from your personal collection'],
    tips: 'Save seeds from your existing plants and grow new ones from them.',
    requiresBackendTracking: true,
  },
  'local-sourcer': {
    criteria: [
      'Source plants from local nurseries',
      'Support local plant sources and garden centers',
    ],
    tips: 'Visit local nurseries and garden centers to source your plants.',
    requiresBackendTracking: true,
  },
  'season-grower': {
    criteria: ['Add plants during all four seasons', 'Maintain year-round gardening activity'],
    tips: 'Track your planting across all seasons to earn this badge.',
    requiresBackendTracking: true,
  },
  'botanical-scholar': {
    criteria: [
      'Ensure all plants have complete taxonomy',
      'Include full botanical classification for your collection',
    ],
    tips: 'Make sure to fill in the botanical information when adding plants to your collection.',
    requiresBackendTracking: true,
  },

  // Milestone badges
  anniversary: {
    criteria: ['Maintain an active PlantQR account for 1 year or more'],
    progressConfig: {
      statsField: 'accountAge',
      maxValue: 365,
      unit: 'days',
    },
  },
  community: {
    criteria: [
      'Share plant sources with the community',
      'Contribute to the shared plant source database',
    ],
    progressConfig: {
      statsField: 'sourcesShared',
      maxValue: 1,
      unit: 'sources shared',
    },
  },
};

// Helper functions
export const getBadgeById = (id: string): Badge | undefined => {
  return BADGE_DEFINITIONS.find(badge => badge.id === id);
};

export const getBadgesByCategory = (category: string): Badge[] => {
  return BADGE_DEFINITIONS.filter(badge => badge.category === category);
};

export const getPlantBadges = (): Badge[] => {
  return getBadgesByCategory('plant');
};

export const getAchievementBadges = (): Badge[] => {
  return getBadgesByCategory('achievement');
};

export const getMilestoneBadges = (): Badge[] => {
  return getBadgesByCategory('milestone');
};

export const getBadgeMetadata = (badgeId: string): BadgeMetadataConfig | undefined => {
  return BADGE_METADATA[badgeId];
};
